import type { TechnicalDocumentType } from "./technical-document-review.js";

/**
 * HOLD — parada formal de um documento em elaboração (Inspeção/Checklist,
 * relatórios END). Motivo obrigatório; o documento aparece no Painel de
 * Engenharia → RELATÓRIOS EM HOLD; a Engenharia comenta e o Inspetor é
 * notificado; "TIRAR DE HOLD" restaura o status de workflow anterior — nunca
 * muda para aprovado. Toda a trilha (iniciado / comentário / liberado) fica no
 * próprio registro.
 */

export type HoldDocumentType = TechnicalDocumentType | "INSPECTION" | "CHECKLIST";

export const HOLD_DOCUMENT_TYPE_LABELS: Record<HoldDocumentType, string> = {
  LP: "LP",
  ME: "ME",
  PM: "PM",
  BSCAN: "B-SCAN",
  INTEGRITY: "INTEGRIDADE",
  AVR: "AVR",
  USC: "US-CONV.",
  INSPECTION: "INSPEÇÃO",
  CHECKLIST: "CHECKLIST"
};

export type HoldCategory =
  | "AGUARDANDO_ACESSO"
  | "AGUARDANDO_DOCUMENTACAO"
  | "AGUARDANDO_PREPARACAO_SUPERFICIE"
  | "AGUARDANDO_LIBERACAO_OPERACIONAL"
  | "DIVERGENCIA_TECNICA"
  | "AGUARDANDO_DECISAO_CLIENTE"
  | "OUTRO";

export const HOLD_CATEGORY_LABELS: Record<HoldCategory, string> = {
  AGUARDANDO_ACESSO: "Aguardando acesso",
  AGUARDANDO_DOCUMENTACAO: "Aguardando documentação",
  AGUARDANDO_PREPARACAO_SUPERFICIE: "Aguardando preparação de superfície",
  AGUARDANDO_LIBERACAO_OPERACIONAL: "Aguardando liberação operacional",
  DIVERGENCIA_TECNICA: "Divergência técnica",
  AGUARDANDO_DECISAO_CLIENTE: "Aguardando decisão do Cliente",
  OUTRO: "Outro"
};

export type HoldStatus = "EM_HOLD" | "LIBERADO";

export const HOLD_STATUS_LABELS: Record<HoldStatus, string> = {
  EM_HOLD: "Em HOLD",
  LIBERADO: "Liberado"
};

export interface HoldActor {
  userId: string;
  professionalId: string | null;
  name: string;
}

export interface HoldDocumentSummary {
  documentNumber: string;
  revision: string;
  clientId: string;
  client: string;
  project: string;
  unitArea: string;
  tag: string;
  osNumber: string;
  subOsNumber: string;
  /** Inspetor responsável — quem recebe os comentários da Engenharia. */
  inspectorProfessionalId: string | null;
  inspectorName: string;
  /** Rota do documento/Checklist que está em HOLD. */
  route: string;
}

export interface HoldEngineeringComment {
  id: string;
  at: string;
  by: HoldActor;
  comment: string;
}

export type HoldHistoryAction = "HOLD_INICIADO" | "COMENTÁRIO_ENGENHARIA" | "HOLD_LIBERADO";

export const HOLD_HISTORY_LABELS: Record<HoldHistoryAction, string> = {
  HOLD_INICIADO: "HOLD iniciado",
  "COMENTÁRIO_ENGENHARIA": "Comentário da Engenharia",
  HOLD_LIBERADO: "HOLD liberado"
};

export interface HoldHistoryEntry {
  at: string;
  by: HoldActor;
  action: HoldHistoryAction;
  detail: string;
}

export interface DocumentHold {
  id: string;
  documentType: HoldDocumentType;
  documentId: string;
  summary: HoldDocumentSummary;
  reason: string;
  category: HoldCategory | null;
  /** Status de workflow do documento no momento do HOLD — é para ele que se volta. */
  previousStatus: string;
  previousStatusLabel: string;
  placedBy: HoldActor;
  placedAt: string;
  status: HoldStatus;
  engineeringComments: HoldEngineeringComment[];
  releasedBy: HoldActor | null;
  releasedAt: string | null;
  releaseNote: string;
  /** Status efetivamente restaurado na liberação (confere com previousStatus). */
  restoredStatus: string | null;
  history: HoldHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export const HOLD_REASON_REQUIRED = "Informe o motivo do HOLD.";
export const HOLD_COMMENT_REQUIRED = "Escreva o comentário da Engenharia.";

export function validateHoldReason(reason: string | null | undefined): string[] {
  return reason && reason.trim() ? [] : [HOLD_REASON_REQUIRED];
}

/** Tempo em HOLD (dias inteiros, mínimo 0). */
export function holdDurationDays(hold: Pick<DocumentHold, "placedAt" | "releasedAt">, now: Date = new Date()): number {
  const start = new Date(hold.placedAt).getTime();
  const end = hold.releasedAt ? new Date(hold.releasedAt).getTime() : now.getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, Math.floor((end - start) / 86400000));
}

export function normalizeDocumentHold(hold: DocumentHold): DocumentHold {
  const h = hold as any;
  if (!Array.isArray(h.engineeringComments)) h.engineeringComments = [];
  if (!Array.isArray(h.history)) h.history = [];
  if (typeof h.releaseNote !== "string") h.releaseNote = "";
  if (h.category === undefined) h.category = null;
  if (h.releasedBy === undefined) h.releasedBy = null;
  if (h.releasedAt === undefined) h.releasedAt = null;
  if (h.restoredStatus === undefined) h.restoredStatus = null;
  return hold;
}
